// ANALYSIS AND CHANGES:
// This is the component for the Add/Edit Position modal.
// RESPONSABILIDADES:
// 1. Render an Ant Design modal.
// 2. Hold a form with every field needed to create or update a `Position`.
// 3. Receive `initialValues` to populate the form in edit mode.
// 4. Use a `useEffect` to reset and populate the form when the modal opens or the initial values change.
// 5. Call `onSave` with the form data when the user clicks save.

import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Divider,
} from "antd";
import { Position } from "@common";
import { useEffect } from "react";

const { TextArea } = Input;

interface AddJobPositionModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (position: any) => void;
  initialValues?: Position | null;
}

export const AddJobPositionModal = ({
  isOpen,
  onCancel,
  onSave,
  initialValues,
}: AddJobPositionModalProps) => {
  const [form] = Form.useForm();
  const isEditing = !!initialValues;

  useEffect(() => {
    if (isOpen) {
      if (isEditing) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
    }
  }, [isOpen, isEditing, initialValues, form]);

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSave({ ...initialValues, ...values });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <Modal
      open={isOpen}
      title={isEditing ? "Edit Position" : "Add New Position"}
      onCancel={onCancel}
      width={800}
      destroyOnClose
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOk} className="btn-double-cut">
          {isEditing ? "Save Changes" : "Create Position"}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" name="job_position_form">
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="title"
              label="Position Title"
              rules={[{ required: true, message: "Please enter the title" }]}
            >
              <Input placeholder="e.g., Snow Removal Operator" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="location"
              label="Location"
              rules={[{ required: true, message: "Please enter the location" }]}
            >
              <Input placeholder="e.g., Tahoe, CA" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="department"
              label="Department"
              rules={[{ required: true, message: "Please select a department" }]}
            >
              <Select placeholder="Select department">
                <Select.Option value="Operations">Operations</Select.Option>
                <Select.Option value="Management">Management</Select.Option>
                <Select.Option value="Snow Removal">Snow Removal</Select.Option>
                <Select.Option value="Maintenance">Maintenance</Select.Option>
                <Select.Option value="Administration">Administration</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Contract Type"
              rules={[{ required: true, message: "Please select a type" }]}
            >
              <Select placeholder="Select contract type">
                <Select.Option value="Full-time">Full-time</Select.Option>
                <Select.Option value="Part-time">Part-time</Select.Option>
                <Select.Option value="Contract">Contract</Select.Option>
                <Select.Option value="Seasonal">Seasonal</Select.Option>
              </Select>
            </Form.Item>
          </Col>
           <Col span={12}>
            <Form.Item
              name="salaryRange"
              label="Salary Range"
              rules={[{ required: true, message: "Please enter the salary" }]}
            >
              <Input placeholder="e.g., $50,000 - $60,000" />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Form.Item name="description" label="Job Description">
          <TextArea rows={4} placeholder="Detailed description of the role, responsibilities, etc." />
        </Form.Item>
         <Form.Item name="featured" valuePropName="checked">
            <input type="checkbox" /> Feature this position?
        </Form.Item>
      </Form>
    </Modal>
  );
};
